<template>
  <div class="content-audit-form-step">

    <v-expansion-panels v-model="expandedPanels" multiple class="form-step-panels">
      <v-expansion-panel
        v-for="{ step, idx } in visibleSteps"
        :key="idx"
        class="mb-4"
      >
        <v-expansion-panel-header class="panel-header-main">
          <div>
            <div class="panel-title mainColor--text font-weight-bold">{{ step.label }}</div>
            <div class="panel-sub text--secondary" style="font-size: 0.85rem;">{{ step.description }}</div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="panel-body">
          <div
            v-for="(field, fIdx) in step.fields"
            :key="field.type === 'field_row' ? 'field-row-' + idx + '-' + fIdx : (field.key || 'static-' + idx + '-' + fIdx)"
            :class="['form-field', { 'form-field-row': field.type === 'field_row', 'form-field-hidden': field.type === 'hidden' }]"
          >
            <template v-if="field.type === 'field_row'">
              <v-row dense class="form-field-row-inner">
                <v-col
                  v-for="sub in field.fields"
                  :key="sub.key"
                  cols="12"
                  sm="6"
                >
                  <template
                    v-if="!sub.show_if || (sub.show_if && formData['step' + idx][sub.show_if.key] === sub.show_if.value)"
                  >
                    <div
                      v-if="sub.label"
                      class="pb-4"
                    >
                      <label
                        :for="sub.key + '-' + idx"
                        class="mainColor--text font-weight-bold"
                      >
                        {{ sub.label }} <span v-if="sub?.validation?.required" style="color:red;">*</span>
                      </label>

                      <div
                        v-if="sub.extra_info_html"
                        class="extra-info"
                        v-html="sub.extra_info_html"
                      />
                    </div>

                    <template v-if="sub.type === 'input'">
                      <v-text-field
                        :id="sub.key + '-' + idx"
                        outlined
                        v-model="v$.formData['step' + idx][sub.key].$model"
                        :readonly="sub.readonly || readonly"
                        :placeholder="sub.placeholder + (!sub?.validation?.required ? ' (Optional)' : '')"
                        :error-messages="v$.formData['step' + idx][sub.key].$errors.map(e => e.$message)"
                        @input="emitReadiness"
                        hide-details="auto"
                      />
                    </template>

                    <template v-if="sub.type === 'textarea'">
                      <v-textarea
                        :id="sub.key + '-' + idx"
                        outlined
                        auto-grow
                        rows="2"
                        v-model="v$.formData['step' + idx][sub.key].$model"
                        :readonly="sub.readonly || readonly"
                        :placeholder="sub.placeholder + (!sub?.validation?.required ? ' (Optional)' : '')"
                        :error-messages="v$.formData['step' + idx][sub.key].$errors.map(e => e.$message)"
                        @input="emitReadiness"
                        hide-details="auto"
                      />
                    </template>

                    <template v-if="sub.type === 'autocomplete'">
                      <v-autocomplete
                        :id="sub.key + '-' + idx"
                        outlined
                        hide-details="auto"
                        :items="sub.autocompleteItems"
                        :item-text="sub.itemText"
                        :item-value="sub.itemValue"
                        :readonly="readonly"
                        v-model="v$.formData['step' + idx][sub.key].$model"
                        :error-messages="v$.formData['step' + idx][sub.key].$errors.map(e => e.$message)"
                        color="grayColor"
                        @input="emitReadiness"
                      />
                    </template>

                    <template v-if="sub.type === 'radio'">
                      <v-radio-group
                        :id="sub.key + '-' + idx"
                        v-model="v$.formData['step' + idx][sub.key].$model"
                        :disabled="readonly"
                        :error-messages="v$.formData['step' + idx][sub.key].$errors.map(e => e.$message)"
                        @change="emitReadiness"
                      >
                        <v-radio
                          v-for="option in sub.options"
                          :key="option.key"
                          color="#FF5C00"
                          :label="option.label"
                          :value="option.key"
                        />
                      </v-radio-group>
                    </template>

                    <template v-if="sub.type === 'radio_buttons'">
                      <v-input
                        class="radio-buttons"
                        :error-messages="v$.formData['step' + idx][sub.key].$errors.map(e => e.$message)"
                      >
                        <v-btn
                          v-for="option in sub.options"
                          :key="option.key"
                          outlined
                          :color="v$.formData['step' + idx][sub.key].$model === option.key ? '#FF5C00' : undefined"
                          :class="{ active: v$.formData['step' + idx][sub.key].$model === option.key }"
                          :disabled="readonly"
                          @click="v$.formData['step' + idx][sub.key].$model = option.key; emitReadiness()"
                        >
                          {{ option.label }}
                        </v-btn>
                      </v-input>
                    </template>

                    <template v-if="sub.type === 'radio_detailed'">
                      <v-input
                        class="radio-detailed"
                        style="max-width: 600px;"
                        :error-messages="v$.formData['step' + idx][sub.key].$errors.map(e => e.$message)"
                      >
                        <div>
                          <div
                            v-for="option in sub.options"
                            :key="option.key"
                            :class="{
                              'radio-detailed-item': true,
                              active: v$.formData['step' + idx][sub.key].$model === option.key
                            }"
                            style="width: 100%;"
                            @click="selectOption(idx, sub.key, option.key)"
                          >
                            <div class="d-flex gap10">
                              <div style="width: 30px; text-align: center;">
                                <div v-if="v$.formData['step' + idx][sub.key].$model === option.key" class="active-icon"><div /></div>
                                <div v-else class="inactive-icon" />
                              </div>
                              <div style="flex: 1;">
                                <div class="radio-detailed-label">{{ option.label }}</div>
                                <div
                                  v-if="option.desc_html"
                                  class="radio-detailed-desc"
                                  v-html="option.desc_html"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </v-input>
                    </template>
                  </template>
                </v-col>
              </v-row>
            </template>
            <template v-else>
              <template
                v-if="!field.show_if || (field.show_if && formData['step' + idx][field.show_if.key] === field.show_if.value)"
              >
                <div
                  v-if="field.label && field.type !== 'hidden' && field.type !== 'slot'"
                  class="pb-4"
                >
                  <label
                    :for="field.key + '-' + idx"
                    class="mainColor--text font-weight-bold"
                  >
                    {{ field.label }} <span v-if="field?.validation?.required || field.required_mark" style="color:red;">*</span>
                    <!-- optional `tooltip` on a field: the info icon + tooltip the Overview stats use, inline
                         with the question (the click is stopped so the label does not focus the input) -->
                    <v-tooltip v-if="field.tooltip" top color="transparent" max-width="420">
                      <template v-slot:activator="{ on, attrs }">
                        <span class="form-field__tooltip-trigger" v-bind="attrs" v-on="on" @click.stop.prevent>
                          <i class="material-icons-outlined" style="font-size: 1rem; color: var(--v-gray40Color-base);">info</i>
                        </span>
                      </template>
                      <div class="white-bg-tooltip small-tooltip p-3" style="line-height: 1.5; white-space: pre-line;">{{ field.tooltip }}</div>
                    </v-tooltip>
                  </label>

                  <div
                    v-if="field.extra_info_html"
                    class="extra-info"
                    v-html="field.extra_info_html"
                  />
                </div>

                <template v-if="field.type === 'input'">
                  <v-text-field
                    :id="field.key + '-' + idx"
                    outlined
                    v-model="v$.formData['step' + idx][field.key].$model"
                    :readonly="field.readonly || readonly"
                    :placeholder="field.placeholder + (!(field?.validation?.required || field.required_mark) ? ' (Optional)' : '')"
                    :error-messages="v$.formData['step' + idx][field.key].$errors.map(e => e.$message)"
                    @input="emitReadiness"
                    hide-details="auto"
                  />
                </template>

                <template v-if="field.type === 'textarea'">
                  <v-textarea
                    :id="field.key + '-' + idx"
                    outlined
                    auto-grow
                    rows="2"
                    v-model="v$.formData['step' + idx][field.key].$model"
                    :readonly="field.readonly || readonly"
                    :placeholder="field.placeholder + (!(field?.validation?.required || field.required_mark) ? ' (Optional)' : '')"
                    :error-messages="v$.formData['step' + idx][field.key].$errors.map(e => e.$message)"
                    @input="emitReadiness"
                    hide-details="auto"
                  />
                </template>

                <!-- static block (POP Authority publishing step boxes); honours show_if like any field -->
                <template v-if="field.type === 'info'">
                  <div class="info-box">
                    <div v-if="field.title" class="info-box__title">
                      <i v-if="field.icon" class="material-icons">{{ field.icon }}</i>
                      <span>{{ field.title }}</span>
                    </div>
                    <div class="info-box__body" v-html="field.html" />
                  </div>
                </template>

                <!-- block rendered by the parent through the scoped slot `field-<key>` (POP Authority
                     Website Publishing Agreement box); a data field like any other (key, initial_value,
                     validation, show_if), its label is only used in the Asana note -->
                <template v-if="field.type === 'slot'">
                  <slot
                    :name="'field-' + field.key"
                    :field="field"
                    :value="v$.formData['step' + idx][field.key].$model"
                    :errors="v$.formData['step' + idx][field.key].$errors.map(e => e.$message)"
                    :readonly="readonly"
                    :set-value="(value) => setFieldValue(idx, field.key, value)"
                  />
                </template>

                <template v-if="field.type === 'autocomplete'">
                  <v-autocomplete
                    :id="field.key + '-' + idx"
                    outlined
                    hide-details="auto"
                    :items="field.autocompleteItems"
                    :item-text="field.itemText"
                    :item-value="field.itemValue"
                    :readonly="readonly"
                    v-model="v$.formData['step' + idx][field.key].$model"
                    :error-messages="v$.formData['step' + idx][field.key].$errors.map(e => e.$message)"
                    color="grayColor"
                    @input="emitReadiness"
                  />
                </template>

                <template v-if="field.type === 'radio'">
                  <v-radio-group
                    :id="field.key + '-' + idx"
                    v-model="v$.formData['step' + idx][field.key].$model"
                    :disabled="readonly"
                    :error-messages="v$.formData['step' + idx][field.key].$errors.map(e => e.$message)"
                    @change="emitReadiness"
                  >
                    <template v-for="option in field.options">
                      <v-radio
                        :key="option.key + '-radio'"
                        color="#FF5C00"
                        :label="option.label"
                        :value="option.key"
                      >
                        <template #label v-if="field.special_label_style">
                          <div style="color: var(--v-mainColor-base) !important; font-weight: 700; font-size: 1rem;">
                            {{ option.label }}
                          </div>
                        </template>
                      </v-radio>
                      <div v-if="option.desc_html" class="radio-desc" v-html="option.desc_html" :key="option.key + '-desc'" />
                    </template>
                  </v-radio-group>
                </template>

                <template v-if="field.type === 'radio_buttons'">
                  <v-input
                    class="radio-buttons"
                    :error-messages="v$.formData['step' + idx][field.key].$errors.map(e => e.$message)"
                  >
                    <v-btn
                      v-for="option in field.options"
                      :key="option.key"
                      outlined
                      :color="v$.formData['step' + idx][field.key].$model === option.key ? '#FF5C00' : undefined"
                      :class="{ active: v$.formData['step' + idx][field.key].$model === option.key }"
                      :disabled="readonly"
                      @click="v$.formData['step' + idx][field.key].$model = option.key; emitReadiness()"
                    >
                      {{ option.label }}
                    </v-btn>
                  </v-input>
                </template>

                <template v-if="field.type === 'radio_detailed'">
                  <v-input
                    class="radio-detailed"
                    style="max-width: 600px;"
                    :error-messages="v$.formData['step' + idx][field.key].$errors.map(e => e.$message)"
                  >
                    <div>
                      <div
                        v-for="option in field.options"
                        :key="option.key"
                        :class="{
                          'radio-detailed-item': true,
                          active: v$.formData['step' + idx][field.key].$model === option.key
                        }"
                        style="width: 100%;"
                        @click="selectOption(idx, field.key, option.key)"
                      >
                        <div class="d-flex gap10">
                          <div style="width: 30px; text-align: center;">
                            <div v-if="v$.formData['step' + idx][field.key].$model === option.key" class="active-icon"><div /></div>
                            <div v-else class="inactive-icon" />
                          </div>
                          <div style="flex: 1;">
                            <div class="radio-detailed-label">{{ option.label }}</div>
                            <div
                              v-if="option.desc_html"
                              class="radio-detailed-desc"
                              v-html="option.desc_html"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-input>
                </template>
              </template>
            </template>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, email, url, helpers } from '@vuelidate/validators'
import { merge } from 'lodash'
import { getHostName } from '@/utils'
import languages from '@/consts/dataforseo_languages.json'
import dataForSeoLocations from '@/consts/dataforseo_locations.json'

export const oneOf = values => helpers.withParams(
  { type: 'oneOf', values },
  value => !helpers.req(value) || values.includes(value)
)

const makeFormSchema = ({
  prevent_campaign_manager_update
}) => {
  void prevent_campaign_manager_update
  return [
    {
      type: 'section',
      label: 'Site info',
      description: 'Your website type and industry',
      fields: [
        {
          type: 'input',
          key: 'email',
          label: 'Your email',
          placeholder: 'name@example.com',
          readonly: true,
          validation: {
            required,
            email
          },
        },
        {
          type: 'input',
          key: 'site_url',
          label: 'Your site URL',
          placeholder: 'https://',
          validation: {
            required,
            url
          },
        },
        {
          type: 'field_row',
          fields: [
            {
              type: 'autocomplete',
              key: 'location_code',
              label: 'Location',
              placeholder: '',
              autocompleteItems: dataForSeoLocations.locations,
              itemText: 'location_name',
              itemValue: 'location_code',
              validation: {
                required,
              },
            },
            {
              type: 'autocomplete',
              key: 'language_code',
              label: 'Language',
              placeholder: '',
              autocompleteItems: languages,
              itemText: 'language_name',
              itemValue: 'language_code',
              validation: {
                required,
              },
            },
          ],
        },
        {
          type: 'radio',
          key: 'site_type',
          label: 'What type of website is this?',
          placeholder: '',
          validation: {
            required,
            oneOf: oneOf(['local_business', 'ecommerce', 'saas', 'marketplace', 'other'])
          },
          options: [
            { key: 'local_business', label: 'Local business' },
            { key: 'ecommerce', label: 'eCommerce' },
            { key: 'saas', label: 'SaaS' },
            { key: 'marketplace', label: 'Marketplace' },
            { key: 'other', label: 'Other (please specify)' },
          ],
          initial_value: 'local_business'
        },
        {
          type: 'input',
          key: 'local_business_desc',
          label: 'Describe your business',
          placeholder: 'What does your business do? Who your customers?',
          show_if: { key: 'site_type', value: 'local_business' }
        },
        {
          type: 'radio',
          key: 'local_business_wants_a_location_based_audit',
          label: 'Location-based audit?',
          extra_info_html: `
          <div class="mt-2 mb-3 darkGrayColor--text">Should we focus on specific locations for your content?</div>
          `,
          placeholder: '',
          validation: {
            oneOf: oneOf(['yes', 'no'])
          },
          options: [
            { key: 'yes', label: 'Yes' },
            { key: 'no', label: 'No' },
          ],
          initial_value: 'yes',
          show_if: { key: 'site_type', value: 'local_business' },
        },
        {
          type: 'input',
          key: 'local_business_locations_prioritized',
          label: 'Service areas',
          placeholder: 'List neighborhoods, cities, or regions',
          extra_info_html: `
          <div class="mt-2 mb-3 darkGrayColor--text">List location from highest to lowest priority</div>
          `,
          show_if: { key: 'site_type', value: 'local_business' }
        },
        {
          type: 'input',
          key: 'other_business_description',
          label: 'Please provide a description of the business',
          placeholder: 'Description',
          show_if: { key: 'site_type', value: 'other' }
        },
        {
          type: 'input',
          key: 'other_business_type',
          label: 'Please specify the type of the website',
          placeholder: 'Description',
          show_if: { key: 'site_type', value: 'other' }
        },
        {
          type: 'input',
          key: 'marketplace_business_description',
          label: 'Describe your marketplace',
          placeholder: 'Description',
          show_if: { key: 'site_type', value: 'marketplace' }
        },
        {
          type: 'input',
          key: 'marketplace_business_type',
          label: 'Please specify focus or niche',
          placeholder: 'Description',
          show_if: { key: 'site_type', value: 'marketplace' }
        },
      ]
    },

    {
      type: 'section',
      label: 'General website info',
      description: 'Tell us about your website and competitors',
      fields: [
        {
          type: 'input',
          key: 'pages_sections_to_prioritize',
          label: 'Do you have specific pages, sections, products or services that MUST be included in the content audit?',
          placeholder: 'Description',
        },
        {
          type: 'input',
          key: 'keywords_topics_to_focus_on',
          label: 'Are there any specific keywords / topics that MUST be included in the content audit?',
          placeholder: 'List keywords or topics',
          validation: {
            required,
          },
        },
        {
          type: 'input',
          key: 'new_top_level_topics_to_research',
          label: 'Do you want us to research any new top-level content? If yes, which topics should we focus on?',
          placeholder: 'List topics',
          validation: {
            required,
          },
        },
        {
          type: 'input',
          key: 'parts_not_to_focus_on',
          label: 'Are there any parts of the website you don’t want us to focus on? (certain types of pages / categories?)',
          placeholder: 'Description',
        },
        {
          type: 'input',
          key: 'godd_piece_of_content_url',
          label: 'Provide example of a good piece of info content currently on the client\'s site (URL)',
          placeholder: 'https://',
          validation: {
            url,
          },
        },
        {
          type: 'input',
          key: 'target_country',
          label: 'What is the site’s target country? (if none leave blank)',
          placeholder: 'Target country',
        },
        {
          type: 'input',
          key: 'target_cities',
          label: 'Do you need to target any specific cities / suburbs? (if so, please list)',
          placeholder: 'List cities or suburbs',
        },
        {
          type: 'input',
          key: 'topics_not_to_cover',
          label: 'Are there any topics you do not want us to cover? (if so, please list)',
          placeholder: 'Description',
        },
        {
          type: 'input',
          key: 'close_industry_competitors',
          label: 'Could you provide 2/3 close industry competitors?',
          placeholder: 'List competitors',
          validation: {
            required,
          },
        },
        {
          type: 'radio',
          key: 'approach',
          label: 'Guidance for audit approach',
          extra_info_html: `
          <div class="mt-2 mb-3 mainColor--text">Please select your preference for how we should conduct the analysis:</div>
          `,
          placeholder: '',
          validation: {
            required,
            oneOf: oneOf(['data_driven', 'user_specified_prioritites_only'])
          },
          special_label_style: true,
          options: [
            { key: 'data_driven', label: 'Data-driven analysis with priority consideration', desc_html: `<div class="mb-6 mainColor--text">We will analyze data to uncover the best opportunities and incorporate your priorities. This approach balances data-driven insights with areas you have identified as essential.</div>` },
            { key: 'user_specified_prioritites_only', label: 'User-specified priorities only', desc_html: `<div class="mainColor--text">We will solely focus on the areas you've specified as essential, disregarding additional opportunities identified through data analysis.</div>` },
          ],
          initial_value: 'data_driven'
        },
        {
          type: 'radio',
          key: 'existing_new_opportunities',
          label: 'Existing vs. New opportunities',
          extra_info_html: `
          <div class="mt-2 mb-3 mainColor--text">Our system prioritizes existing opportunities when a site has enough pages. However, if you are looking for new opportunities beyond your current scope, we can provide recommendations.</div>

          <div class="mb-1 mainColor--text font-weight-bold" style="font-size: 1rem;">Would you like us to focus on existing opportunities, or would you prefer to explore new ones too?</div>
          `,
          placeholder: '',
          validation: {
            required,
            oneOf: oneOf(['identify_best', 'focus_existing', 'mix'])
          },
          options: [
            { key: 'identify_best', label: 'Identify the best opportunities based on data analysis' },
            { key: 'focus_existing', label: 'Focus on existing opportunities only' },
            { key: 'mix', label: 'Provide a mix of existing and new opportunities' },
          ],
          initial_value: 'identify_best'
        },
      ]
    },

    {
      type: 'section',
      label: 'Publishing',
      description: 'How you publish content on your site',
      fields: [
        {
          type: 'input',
          key: 'process_to_get_content_published',
          label: 'What is the process for you to get content published on the clients site? (e.g. Do you have access and permission to publish? Or is there a lengthy approval process?',
          placeholder: 'Description',
          validation: {
            required,
          },
        },
        {
          type: 'input',
          key: 'need_assistance_publishing',
          label: 'Do you need assistance getting content published on the site?',
          placeholder: 'Description',
        },
        {
          type: 'input',
          key: 'cms_used',
          label: 'If so, what web builder or CMS is being used?',
          placeholder: 'Description',
        },
        {
          type: 'input',
          key: 'monthly_quotas',
          label: 'Do you have any monthly quantity quotas for how many pages / content pieces to publish per month for the client?',
          placeholder: 'Description',
        },
      ]
    }
  ]
}

function flattenFields (fields) {
  const out = []
  for (const field of fields) {
    if (field.type === 'field_row' && Array.isArray(field.fields)) {
      out.push(...field.fields)
    } else {
      out.push(field)
    }
  }
  return out
}

/** Fields that carry a value (static `info` blocks have no key and no data). */
function dataFields (fields) {
  return flattenFields(fields).filter(field => field.type !== 'info' && field.key)
}

const getInitialFormData = (formSchema) => Object.fromEntries(
  formSchema.map((step, idx) => ([
    'step' + idx,
    Object.fromEntries(dataFields(step.fields).map(field => [field.key, field?.initial_value]))
  ]))
)

export default {
  setup () {
    return { v$: useVuelidate() }
  },
  props: {
    site: { type: Object, default: null },
    activeAudit: { type: Object, default: null },
    /** Full form schema override (POP Authority). Default: the White Glove audit form. */
    schema: { type: Array, default: null },
    /** Indexes of the schema sections to render/validate; data keys stay `step<index>` of the full schema. */
    sections: { type: Array, default: null },
    /** Render every field disabled (revisiting a submitted step). */
    readonly: { type: Boolean, default: false },
    /**
     * 'submit' (default): today's behaviour, the form is submitted (status form_submitted, Asana note,
     * staff email) and the domain-level campaign is created.
     * 'draft': answers are stored without submitting (POP Authority "Save & continue").
     */
    submitMode: { type: String, default: 'submit' },
    /** Open every rendered section on mount (POP Authority wizard). */
    expandAll: { type: Boolean, default: false },
  },
  data () {
    return {
      expandedPanels: [],
      formData: [],
      submitting: false,
      /** The backend's `msg` when the last submit was refused, '' otherwise (read by the POP Authority steps). */
      submitError: '',
    }
  },
  computed: {
    formSchema: function () {
      if (this.schema) {
        return this.schema
      }
      return makeFormSchema({
        prevent_campaign_manager_update: this.user?.prevent_campaign_manager_update
      });
    },
    /** Rendered sections with their index in the full schema. */
    visibleSteps () {
      return this.formSchema
        .map((step, idx) => ({ step, idx }))
        .filter(({ idx }) => !this.sections || this.sections.includes(idx))
    },
    user () {
      if (this.$store.state.user) {
        return this.$store.state.user
      }
      return null
    },
    selectedSubAccountRelation () {
      return this.$store.state.selectedSubAccountRelation
    },
    isFormValid: function () {
      return !this.v$.formData.$invalid
    },
  },
  watch: {
    activeAudit: {
      handler: function (newVal) {
        this.formData = merge(getInitialFormData(this.formSchema), newVal?.formData || {})
        this.applyDefaultsForNewFields()
      },
      immediate: true,
    },
  },
  validations () {
    // Only the rendered sections are validated (a partial POP Authority step must not be blocked
    // by required fields of a section the user has not reached yet).
    return {
      formData: Object.fromEntries(
        this.visibleSteps.map(({ step, idx }) => ([
          'step' + idx,
          Object.fromEntries(dataFields(step.fields).map(field => [field.key, field.validation || {}]))
        ]))
      )
    }
  },
  methods: {
    emitReadiness () {
      this.$nextTick(() => {
        this.$emit('readiness-change')
      })
    },
    /** radio_detailed option click (no-op while read-only). */
    selectOption (idx, key, optionKey) {
      if (this.readonly) return
      this.v$.formData['step' + idx][key].$model = optionKey
      this.emitReadiness()
    },
    /** Value change of a `slot` field (set by the parent's block). */
    setFieldValue (idx, key, value) {
      this.v$.formData['step' + idx][key].$model = value
      this.emitReadiness()
    },
    applyDefaultsForNewFields () {
      const step0 = this.formData.step0
      if (step0 && (step0.location_code === undefined || step0.location_code === null || step0.location_code === '')) {
        this.$set(this.formData.step0, 'location_code', 2840)
      }
      if (step0 && !step0.language_code) {
        this.$set(this.formData.step0, 'language_code', 'en')
      }
    },
    /** `hidden` fields with `derive: 'location_name'` get the step0 location's name (e.g. target_country). */
    fillDerivedFields () {
      const code = this.formData.step0?.location_code
      const location = dataForSeoLocations.locations.find(l => String(l.location_code) === String(code))
      this.formSchema.forEach((step, idx) => {
        dataFields(step.fields).forEach((field) => {
          if (field.type === 'hidden' && field.derive === 'location_name' && location) {
            this.$set(this.formData['step' + idx], field.key, location.location_name)
          }
        })
      })
    },
    async submitForm () {
      if (!this.isFormValid || this.submitting) {
        return false
      }
      this.v$.formData.$touch()
      if (this.v$.formData.$invalid) {
        return false
      }

      this.submitting = true
      this.submitError = ''
      const hadCampaign = Boolean(this.activeAudit?.hasAssociatedDomainLevelCampaign)

      try {
        this.fillDerivedFields()

        if (this.submitMode === 'draft') {
          const draftResponse = await this.$store.dispatch('updateSiteContentAudit', {
            auditId: this.activeAudit.id,
            body: {
              formData: this.formData,
              saveDraft: true,
              siteInfoCompleted: true,
            }
          })
          if (!(draftResponse.data && draftResponse.data.status === 'SUCCESS')) {
            this.submitError = (draftResponse.data && draftResponse.data.msg) || ''
            return false
          }
          this.$emit('auditUpdated', draftResponse.data.audit)
          return true
        }

        const response = await this.$store.dispatch('updateSiteContentAudit', {
          auditId: this.activeAudit.id,
          body: {
            formData: this.formData,
            formSchema: JSON.stringify(this.formSchema),
          }
        })

        if (!(response.data && response.data.status === 'SUCCESS')) {
          this.submitError = (response.data && response.data.msg) || ''
          return false
        }

        this.$emit('auditUpdated', response.data.audit)

        if (!hadCampaign) {
          const loc = this.formData.step0.location_code
          const lang = this.formData.step0.language_code
          const cr = await this.$store.dispatch('createKeywordResearchCampaign', {
            campaignName: '',
            siteId: this.site.id,
            targetUrl: getHostName(this.site.baseURL),
            isDomainLevelCampaign: true,
            locationCode: typeof loc === 'string' ? parseInt(loc, 10) : loc,
            languageCode: lang,
          })
          if (!(cr.data && cr.data.status === 'SUCCESS')) {
            this.$notify({
              group: 'info',
              type: 'error',
              text: (cr.data && cr.data.msg) ? cr.data.msg : 'Could not create keyword research campaign for this site.'
            })
            return false
          }
        }

        return true
      } catch (e) {
        console.error(e)
        // a 4xx answer (the API client throws on it) carries the backend's reason
        this.submitError = (e && e.response && e.response.data && e.response.data.msg) || ''
        return false
      } finally {
        this.submitting = false
      }
    },
  },
  mounted () {
    if (this.expandAll) {
      this.expandedPanels = this.visibleSteps.map((_, i) => i)
    }
    const step0Rules = this.v$.formData.step0
    if (step0Rules && step0Rules.email && !this.formData.step0?.email) {
      step0Rules.email.$model = this?.selectedSubAccountRelation?.agencyUserEmail || this.user?.email
    }
    if (step0Rules && step0Rules.site_url && !this.formData.step0?.site_url) {
      step0Rules.site_url.$model = this.site.baseURL
    }
    this.applyDefaultsForNewFields()
    this.emitReadiness()
  },
}
</script>

<style lang="scss" scoped>
.content-audit-form-step {
  .form-step-panels {
    overflow: hidden;
  }

  .v-expansion-panel {
    border: 1px solid #ddd;
    border-radius: 8px;

    &::before {
      box-shadow: none;
    }
  }

  .panel-header-main {
    min-height: 56px !important;

    .panel-title {
      font-size: 1.1rem !important;
      font-weight: 700 !important;
      margin-bottom: 10px !important;
    }
  }

  .panel-body {
    padding-top: 16px !important;
  }

  .form-field {
    margin-bottom: 28px;

    &.form-field-hidden {
      display: none;
    }

    label {
      font-size: 1rem !important;
    }

    .extra-info * {
      font-size: 0.9rem;
    }

    .form-field__tooltip-trigger {
      display: inline-flex;
      align-items: center;
      margin-left: 4px;
      vertical-align: middle;
      cursor: default;
    }

    .v-input--selection-controls {
      margin-top: 0;
    }

    .v-text-field .v-input__slot {
      border: 0.84px solid #ddd;
      box-shadow: none !important;
    }

    .v-text-field {
      ::v-deep fieldset {
        border-color: var(--v-gray12Color-base);
      }
    }

    .v-text-field input {
      font-size: 0.9rem !important;
    }

    .v-text-field.v-input--is-readonly * {
      cursor: not-allowed;
    }

    .info-box {
      border: 1px solid var(--v-gray15Color-base, #e4e4e4);
      background: var(--v-gray7Color-base, #f7f7f7);
      border-radius: 10px;
      padding: 14px 16px;
      max-width: 600px;

      &__title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 800;
        color: var(--v-mainColor-base);
        margin-bottom: 8px;

        .material-icons {
          font-size: 19px;
          color: var(--v-link3Color-base, #316bff);
        }
      }

      &__body {
        font-size: 0.88rem;
        line-height: 1.5;
        color: var(--v-gray2Color-base, #757575);

        b {
          color: var(--v-mainColor-base);
        }
      }
    }

    .v-radio .v-label {
      color: var(--v-mainColor-base);
    }

    .radio-buttons .v-input__slot {
      display: flex;
      flex-direction: row;
      gap: 8px;
    }

    .radio-buttons .v-btn {
      text-transform: initial;
      border-color: #ddd;

      &.active {
        background-color: #fff4ee;
        border-color: #ff5c00;
        font-weight: 700;
      }
    }

    .radio-detailed .v-input__slot {
      padding: 0 !important;
    }

    .radio-detailed-item {
      margin-bottom: 10px;
      padding: 16px;
      border-radius: 4px;
      border: 1px solid #ccc;
      background-color: #fff;
      box-shadow: 0 2px 4px 0 #0000001a;
      cursor: pointer;
      color: #014751;

      &.active {
        border-color: #ff5c00;
        background-color: #fff4ee;
      }

      .active-icon {
        display: inline-flex;
        width: 22px;
        height: 22px;
        border-radius: 100%;
        border: 2px solid #ff5c00;
        align-items: center;
        justify-content: center;

        div {
          display: inline-block;
          width: 12px;
          height: 12px;
          border-radius: 100%;
          background-color: #ff5c00;
        }
      }

      .inactive-icon {
        display: inline-flex;
        width: 22px;
        height: 22px;
        border-radius: 100%;
        border: 1px solid #ccc;
        align-items: center;
        justify-content: center;
      }

      .radio-detailed-label {
        font-size: 1.1rem;
        font-weight: 700;
        margin-bottom: 8px;
      }

      .radio-detailed-desc {
        font-size: 0.9rem;
        line-height: 1.6;
      }
    }

    &.form-field-row {
      .form-field-row-inner .col:not(:last-child) {
        @media (max-width: 599px) {
          padding-bottom: 8px;
        }
      }
    }
  }
}
</style>
